/**
 * Custom hooks per autenticazione
 * Gestisce login, logout, refresh token e informazioni utente corrente
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { apiGet, apiPost, removeAuthToken, setAuthToken, apiClient } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import { toast } from "@/lib/toast-service";
import { CompanyType } from "@/types/api/admin";
import type { LoginRequest, MessageResponse, RefreshRequest, TokenResponse, UserInfo } from "@/types/api/auth";

/**
 * Switch to a different company
 */
export async function switchCompany(companyId: string): Promise<TokenResponse> {
    return await apiPost<TokenResponse>("/auth/switch-company", {
        company_id: companyId,
    });
}

const AUTH_QUERY_KEYS = {
    me: ["auth", "me"] as const,
};

/**
 * Helper per ottenere il base path in base al company type
 * Normalizza a lowercase per gestire sia 'sender' che 'SENDER' dal backend
 */
export function getBasePathForCompanyType(companyType: CompanyType | string): string {
    const normalizedType = String(companyType).toLowerCase();
    switch (normalizedType) {
        case 'sender':
            return '/shipper';
        case 'receiver':
            return '/receiver';
        case 'freight_forwarder':
            return '/freight-forwarder';
        default:
            return '';
    }
}

/**
 * Determina la route di redirect in base al ruolo e company_type dell'utente
 * @param user Informazioni utente
 * @param menuConfig Configurazione menu opzionale per determinare quale pagina è abilitata
 * @returns Route di redirect o null se il tipo di utente non ha una sezione navigabile
 */
export function getRedirectPath(user: UserInfo, menuConfig?: Record<string, boolean>): string | null {
    // SOLO super_admin va a /admin/dashboard
    if (user.role === "super_admin") {
        return "/admin/dashboard";
    }

    // Tutti gli altri ruoli vanno in base al company_type
    if (!user.company_type) {
        return null; // company_type non disponibile o non supportato
    }

    const companyType = user.company_type;
    const basePath = getBasePathForCompanyType(companyType);

    // Se non abbiamo un base path valido, ritorna null
    if (!basePath) {
        return null;
    }

    // Se menuConfig non è disponibile: landing su Control Hub (Ask Eve è nel FAB / pannello)
    if (!menuConfig) {
        return `${basePath}/control-hub`;
    }

    // Preferisci Control Hub quando non disabilitato esplicitamente (legacy: chiave `dashboard` in DB)
    const controlHubAllowed =
        menuConfig['controlHub'] !== false && menuConfig['dashboard'] !== false;
    if (controlHubAllowed) {
        return `${basePath}/control-hub`;
    }

    // Fallback a containers
    return `${basePath}/containers`;
}

/**
 * Hook per login
 */
export function useLogin() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (credentials: LoginRequest): Promise<TokenResponse> => {
            const response = await apiPost<TokenResponse>("/auth/login", credentials, { requireAuth: false });

            // Salva token nel localStorage
            setAuthToken(response.access_token);
            storage.setItem("refresh_token", response.refresh_token);

            return response;
        },
        onSuccess: async (data) => {
            // Invalida e aggiorna query utente corrente
            queryClient.setQueryData(AUTH_QUERY_KEYS.me, data.user);

            // Mostra toast di successo
            toast.success("Login effettuato con successo");

            // Carica menu configuration se disponibile
            let menuConfig: Record<string, boolean> | undefined;
            if (data.user.company_id) {
                try {
                    const configData = await apiClient<{ items: Record<string, boolean> }>(
                        `/menu-configurations/by-company/${data.user.company_id}`,
                        { requireAuth: true }
                    );
                    menuConfig = configData.items;
                } catch (error) {
                    // Se il caricamento fallisce, usa comportamento di default
                    console.warn('Failed to load menu configuration, using default', error);
                }
            }

            // Determina la route di redirect
            const redirectPath = getRedirectPath(data.user, menuConfig);

            if (redirectPath === null) {
                // Genera messaggio di errore specifico
                let errorMsg: string;
                if (!data.user.company_id) {
                    errorMsg = "Il tuo account non è associato a nessuna azienda. Contatta l'amministratore per essere assegnato a un'azienda.";
                } else if (!data.user.company_type) {
                    errorMsg = "L'azienda associata al tuo account non ha un tipo configurato. Contatta l'amministratore.";
                } else {
                    errorMsg = `Il tipo di azienda "${data.user.company_type}" non ha una sezione navigabile disponibile. Contatta l'amministratore.`;
                }
                throw new Error(errorMsg);
            }

            // Redirect alla route corretta
            navigate(redirectPath);
        },
        onError: (error) => {
            // L'errore verrà gestito dal componente che usa useLogin
            // Non facciamo logout qui - l'utente rimane autenticato
            console.error("Login error:", error);
        },
    });
}

/**
 * Hook per logout
 */
export function useLogout() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (): Promise<MessageResponse> => {
            return await apiPost<MessageResponse>("/auth/logout");
        },
        onSuccess: () => {
            // Rimuovi token e dati di autenticazione
            removeAuthToken();
            
            // Rimuovi anche dati utente correlati (se presenti)
            storage.removeItems(["userType", "accountId", "accountName"]);

            // Invalida tutte le query, incluso useMe
            queryClient.clear();
            queryClient.removeQueries({ queryKey: AUTH_QUERY_KEYS.me });

            // Redirect al login
            navigate("/auth/login");
        },
        onError: () => {
            // Anche in caso di errore, rimuovi token e dati di autenticazione
            removeAuthToken();
            
            // Rimuovi anche dati utente correlati (se presenti)
            storage.removeItems(["userType", "accountId", "accountName"]);

            // Invalida tutte le query, incluso useMe
            queryClient.clear();
            queryClient.removeQueries({ queryKey: AUTH_QUERY_KEYS.me });
            
            navigate("/auth/login");
        },
    });
}

/**
 * Hook per ottenere informazioni utente corrente
 */
export function useMe() {
    // Verifica se c'è un token prima di abilitare la query
    const hasToken = storage.getItem("access_token") !== null;
    
    return useQuery({
        queryKey: AUTH_QUERY_KEYS.me,
        queryFn: async (): Promise<UserInfo> => {
            return await apiGet<UserInfo>("/auth/me");
        },
        retry: false, // Non ritentare se fallisce (probabilmente token scaduto)
        staleTime: 5 * 60 * 1000, // 5 minuti
        enabled: hasToken, // Non fare la chiamata se non c'è token
    });
}

/**
 * Hook per refresh token manuale
 *
 * Nota: Il refresh automatico del token è gestito automaticamente in api-client.ts
 * quando una richiesta riceve un 401 Unauthorized. Questo hook è disponibile
 * per casi d'uso specifici che richiedono un refresh manuale.
 */
export function useRefreshToken() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (refreshToken: string): Promise<TokenResponse> => {
            const request: RefreshRequest = { refresh_token: refreshToken };
            const response = await apiPost<TokenResponse>("/auth/refresh", request, { requireAuth: false });

            // Salva nuovo token
            setAuthToken(response.access_token);
            storage.setItem("refresh_token", response.refresh_token);

            return response;
        },
        onSuccess: (data) => {
            // Aggiorna query utente corrente
            queryClient.setQueryData(AUTH_QUERY_KEYS.me, data.user);
        },
    });
}
